/**
 * Plain-words summaries for the Staff page's read-only cards (DNKADM-0494). Pure text from
 * data the page already reads; nothing here writes.
 */
import type {
  CapPeriod,
  StaffAppointmentCap,
  StaffDateException,
  StaffScheduleDay,
  StaffShift,
} from '@/api/staff';
import { clock12, dateLabel } from '@/utils/clockTime';

/** Short day names indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `"10 AM"` / `"10:30 AM"` from a `HH:mm` wall clock; the raw text when it cannot be read. */
export function shortClock(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!match) return hhmm;
  return clock12(Number(match[1]), Number(match[2])).replace(':00 ', ' ');
}

function shiftsText(shifts: StaffShift[]): string {
  return shifts.map((s) => `${shortClock(s.startTime)} to ${shortClock(s.endTime)}`).join(' and ');
}

/** A day's hours as a comparable key (`''` = not working that day). */
function dayKey(day: StaffScheduleDay | undefined): string {
  if (!day || !day.isAvailable || day.shifts.length === 0) return '';
  return shiftsText(day.shifts);
}

/**
 * One location's week in words, e.g. `"Tue to Thu 10 AM to 7 PM"`. Neighboring days with the
 * same hours are grouped (three or more read "Tue to Thu", two read "Mon, Tue"); groups are
 * joined with "; ". `"No hours set"` when no day has hours.
 */
export function weeklyHoursSummary(days: StaffScheduleDay[]): string {
  const byDay = new Map(days.map((d) => [d.dayOfWeek, d] as const));
  const groups: { start: number; end: number; hours: string }[] = [];
  for (let dow = 0; dow < 7; dow += 1) {
    const hours = dayKey(byDay.get(dow));
    if (hours === '') continue;
    const last = groups[groups.length - 1];
    if (last && last.end === dow - 1 && last.hours === hours) last.end = dow;
    else groups.push({ start: dow, end: dow, hours });
  }
  if (groups.length === 0) return 'No hours set';
  return groups
    .map(({ start, end, hours }) => {
      const first = SHORT_DAYS[start] ?? '';
      const lastDay = SHORT_DAYS[end] ?? '';
      const span =
        start === end ? first : end - start === 1 ? `${first}, ${lastDay}` : `${first} to ${lastDay}`;
      return `${span} ${hours}`;
    })
    .join('; ');
}

/** A day off's date as shown (`"Mar 4, 2027"`), or the raw value when it cannot be read. */
export function dayOffDate(exception: StaffDateException): string {
  return dateLabel(exception.exceptionDate) ?? exception.exceptionDate;
}

/** What a day off means, in words: `"Off, all locations"` or its special hours and place. */
export function dayOffLine(exception: StaffDateException, locationName: (id: string) => string): string {
  const what =
    exception.isAvailable && exception.shifts.length > 0
      ? `Special hours ${shiftsText(exception.shifts)}`
      : exception.isAvailable
        ? 'Available'
        : 'Off';
  const where =
    exception.scope === 'LOCATION' && exception.locationId !== null
      ? locationName(exception.locationId)
      : 'all locations';
  return `${what}, ${where}`;
}

/** The label for a limit's period, as the Limits drawer names it. */
export const CAP_PERIOD_LABELS: Record<CapPeriod, string> = {
  day: 'Per day',
  week: 'Per week (resets Sunday)',
  month: 'Per calendar month',
};

/** One limit's value in words: `"Up to 4 appointments"`. */
export function capLine(cap: StaffAppointmentCap): string {
  const n = cap.maxAppointments;
  return `Up to ${n} appointment${n === 1 ? '' : 's'}`;
}
